import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";

interface LoadItem {
  inventoryItemId: number;
  panelId: number;
  productName: string;
  markNumber: string;
  weight: number;
  dimensions: string;
  location: string;
}

interface ShipmentData {
  jobId: number | null;
  truckId: number | null;
  trailerId: number | null;
  driverId: number | null;
  loadNumber: string;
  scheduledDateTime: Date;
  deliveryAddress: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryZip: string;
  contactName: string;
  contactPhone: string;
  specialInstructions: string;
  loadItems: LoadItem[];
}

class ShipmentPlanningWizardStore {
  rootStore: RootStore;

  // Observables
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  shipmentData: ShipmentData = {
    jobId: null,
    truckId: null,
    trailerId: null,
    driverId: null,
    loadNumber: "",
    scheduledDateTime: new Date(),
    deliveryAddress: "",
    deliveryCity: "",
    deliveryState: "",
    deliveryZip: "",
    contactName: "",
    contactPhone: "",
    specialInstructions: "",
    loadItems: [],
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  updateShipmentData = <K extends keyof ShipmentData>(
    field: K,
    value: ShipmentData[K]
  ) => {
    this.shipmentData[field] = value;
  };

  selectJob = (jobId: number) => {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    const job = jobStore?.entities.find((j: any) => j.id === jobId) as unknown as
      | { address?: string; city?: string; stateLocation?: string; zip?: string }
      | undefined;

    runInAction(() => {
      this.shipmentData.jobId = jobId;
      if (job) {
        this.shipmentData.deliveryAddress = job.address || "";
        this.shipmentData.deliveryCity = job.city || "";
        this.shipmentData.deliveryState = job.stateLocation || "";
        this.shipmentData.deliveryZip = job.zip || "";
      }
    });
  };

  addItem = (inventoryItem: any) => {
    // Check if item already added
    if (this.shipmentData.loadItems.find((item) => item.inventoryItemId === inventoryItem.id)) {
      this.setError("Item already added to load");
      return false;
    }

    const panelStore = this.rootStore.panelStore as ServiceStore;
    const productsStore = this.rootStore.productsStore as ServiceStore;
    const yardLocationsStore = this.rootStore.yardLocationsStore as ServiceStore;

    const panel = panelStore?.entities.find((p: any) => p.productId === inventoryItem.productId) as unknown as
      | { id?: number; markNumber?: string }
      | undefined;
    const product = productsStore?.entities.find((p: any) => p.id === inventoryItem.productId) as unknown as
      | { name?: string; weight?: number; length?: number; width?: number; thickness?: number }
      | undefined;
    const location = yardLocationsStore?.entities.find((l: any) => l.id === inventoryItem.currentLocationId) as unknown as
      | { aisle?: string; row?: string; position?: string }
      | undefined;

    const loadItem: LoadItem = {
      inventoryItemId: inventoryItem.id,
      panelId: panel?.id || 0,
      productName: product?.name || "Unknown Product",
      markNumber: panel?.markNumber || "N/A",
      weight: product?.weight || 0,
      dimensions: `${product?.length || 0} x ${product?.width || 0} x ${product?.thickness || 0}`,
      location: location ? `${location.aisle}-${location.row}${location.position ? `-${location.position}` : ""}` : "Unknown",
    };

    runInAction(() => {
      this.shipmentData.loadItems = [...this.shipmentData.loadItems, loadItem];
    });

    return true;
  };

  removeItem = (inventoryItemId: number) => {
    this.shipmentData.loadItems = this.shipmentData.loadItems.filter(
      (item) => item.inventoryItemId !== inventoryItemId
    );
  };

  generateLoadNumber = (): string => {
    const date = new Date();
    const dateStr = `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, "0")}${date.getDate().toString().padStart(2, "0")}`;
    const sequence = Math.floor(Math.random() * 999) + 1;
    return `LOAD-${dateStr}-${sequence.toString().padStart(3, "0")}`;
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0: // Job & Equipment Selection
        if (!this.shipmentData.jobId) {
          this.setError("Please select a job");
          return false;
        }
        if (!this.shipmentData.trailerId) {
          this.setError("Please select a trailer");
          return false;
        }
        if (!this.shipmentData.driverId) {
          this.setError("Please assign a driver");
          return false;
        }
        break;

      case 1: // Load Planning
        if (this.shipmentData.loadItems.length === 0) {
          this.setError("Please add at least one item to the load");
          return false;
        }
        if (this.totalWeight === 0) {
          this.setError("Load weight cannot be zero");
          return false;
        }
        break;

      case 2: // Delivery Information
        if (!this.shipmentData.deliveryAddress) {
          this.setError("Please provide delivery address");
          return false;
        }
        if (!this.shipmentData.deliveryCity) {
          this.setError("Please provide delivery city");
          return false;
        }
        if (!this.shipmentData.deliveryState) {
          this.setError("Please provide delivery state");
          return false;
        }
        if (!this.shipmentData.contactName) {
          this.setError("Please provide contact name");
          return false;
        }
        if (!this.shipmentData.contactPhone) {
          this.setError("Please provide contact phone number");
          return false;
        }
        break;
    }

    return true;
  };

  // Async actions
  loadInitialData = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const jobStore = this.rootStore.jobStore as ServiceStore;
      const trailerStore = this.rootStore.trailerStore as ServiceStore;
      const userStore = this.rootStore.userStore as ServiceStore;
      const yardInventoryItemsStore = this.rootStore.yardInventoryItemsStore as ServiceStore;
      const yardLocationsStore = this.rootStore.yardLocationsStore as ServiceStore;
      const panelStore = this.rootStore.panelStore as ServiceStore;
      const productsStore = this.rootStore.productsStore as ServiceStore;
      const salesOrdersStore = this.rootStore.salesOrdersStore as ServiceStore;

      await Promise.all([
        jobStore?.find(),
        trailerStore?.find(),
        userStore?.find(),
        yardInventoryItemsStore?.find(),
        yardLocationsStore?.find(),
        panelStore?.find(),
        productsStore?.find(),
        salesOrdersStore?.find(),
      ]);
    } catch (err: any) {
      runInAction(() => {
        this.setError("Failed to load initial data");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  createShipment = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const loadStore = this.rootStore.loadStore as ServiceStore;
      const trailerStore = this.rootStore.trailerStore as ServiceStore;
      const yardInventoryItemsStore = this.rootStore.yardInventoryItemsStore as ServiceStore;
      const jobStore = this.rootStore.jobStore as ServiceStore;

      const loadNumber = this.shipmentData.loadNumber || this.generateLoadNumber();

      // Create load record
      const newLoad = await loadStore?.create({
        jobNumber: jobStore?.entities.find((j: any) => j.id === this.shipmentData.jobId)?.jobNumber,
        trailerNumber: trailerStore?.entities.find((t: any) => t.id === this.shipmentData.trailerId)?.number,
        loadNumber,
        checkedBy: this.drivers.find((d: any) => d.id === this.shipmentData.driverId)?.name || "Unknown",
        numberOfDrivers: 1,
        loadDate: this.shipmentData.scheduledDateTime.toISOString().split("T")[0],
        loadTime: this.shipmentData.scheduledDateTime.toTimeString().split(" ")[0],
      });

      if (!newLoad) {
        throw new Error("Failed to create load");
      }

      const newLoadEntity = (Array.isArray(newLoad) ? newLoad[0] : newLoad) as { id: string | number };

      // Update inventory items with load ID
      for (const item of this.shipmentData.loadItems) {
        await yardInventoryItemsStore?.patch(item.inventoryItemId, {
          dispatchLoadId: newLoadEntity.id,
        });
      }

      // Update trailer status
      if (this.shipmentData.trailerId) {
        await trailerStore?.patch(this.shipmentData.trailerId, {
          status: "in_use",
          currentLoadId: newLoadEntity.id,
        });
      }

      runInAction(() => {
        this.setSuccess("Shipment created successfully!");
        this.updateShipmentData("loadNumber", loadNumber);
        this.nextStep();
      });
    } catch (err: any) {
      runInAction(() => {
        this.setError(err.message || "Failed to create shipment");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  printBOL = () => {
    // TODO: Generate and print Bill of Lading
    window.print();
  };

  emailBOL = () => {
    // TODO: Email Bill of Lading to customer
    console.log("Emailing BOL to:", this.shipmentData.contactName);
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.shipmentData = {
      jobId: null,
      truckId: null,
      trailerId: null,
      driverId: null,
      loadNumber: "",
      scheduledDateTime: new Date(),
      deliveryAddress: "",
      deliveryCity: "",
      deliveryState: "",
      deliveryZip: "",
      contactName: "",
      contactPhone: "",
      specialInstructions: "",
      loadItems: [],
    };
  };

  // Computed values
  get jobs() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    return jobStore?.entities || [];
  }

  get trailers() {
    const trailerStore = this.rootStore.trailerStore as ServiceStore;
    return trailerStore?.entities.filter((t: any) => t.status === "available") || [];
  }

  get drivers() {
    const userStore = this.rootStore.userStore as ServiceStore;
    return userStore?.entities.filter((u: any) => u.role === "Driver") || [];
  }

  get inventoryItems() {
    const yardInventoryItemsStore = this.rootStore.yardInventoryItemsStore as ServiceStore;
    return yardInventoryItemsStore?.entities.filter(
      (item: any) => item.qcStatus === "passed" && !item.dispatchLoadId
    ) || [];
  }

  get products() {
    const productsStore = this.rootStore.productsStore as ServiceStore;
    return productsStore?.entities || [];
  }

  get yardLocations() {
    const yardLocationsStore = this.rootStore.yardLocationsStore as ServiceStore;
    return yardLocationsStore?.entities || [];
  }

  get availableItems() {
    if (!this.shipmentData.jobId) return [];
    const panelStore = this.rootStore.panelStore as ServiceStore;

    return this.inventoryItems.filter((item: any) => {
      const panel = panelStore?.entities.find((p: any) => p.productId === item.productId);
      return panel?.jobId === this.shipmentData.jobId;
    });
  }

  get totalWeight() {
    return this.shipmentData.loadItems.reduce((total, item) => total + item.weight, 0);
  }

  get canProceedFromStep1() {
    return !!this.shipmentData.jobId && !!this.shipmentData.trailerId && !!this.shipmentData.driverId;
  }

  get canProceedFromStep2() {
    return this.shipmentData.loadItems.length > 0;
  }

  get selectedJob() {
    if (!this.shipmentData.jobId) return null;
    return this.jobs.find((j: any) => j.id === this.shipmentData.jobId);
  }

  get selectedDriver() {
    if (!this.shipmentData.driverId) return null;
    return this.drivers.find((d: any) => d.id === this.shipmentData.driverId);
  }
}

export default ShipmentPlanningWizardStore;